import { createClient, onCameraChanged, onMicrophoneChanged, } from "agora-rtc-sdk-ng/esm"
import { useCallback, useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Box, ThemeProvider, Typography, useTheme } from '@mui/material'
import { observer } from 'mobx-react'

import DiscussionMeetingParticipationLayout from '../../components/webinar-room/DiscussionMeetingParticipationLayout.jsx'
import WebinarMeetingParticipationLayout from '../../components/webinar-room/WebinarMeetingParticipationLayout.jsx'
import MeetingStoreInstance, { MeetingStoreProvider, useMeetingStore } from '../../store/meeting.store'
import VitualboardStoreInstance, { useVirtualboardStore, VirtualboardProvider } from '../../store/virtualboard.store.jsx'
import SocketsContextProvider, { useSocketsContext } from '../../contexts/sockets.context'
import OnlineMeetingTimer from '../../components/webinar-room/OnlineMeetingTimer.jsx'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import AGORA_CONFIG from '../../agoraConfig.js'
import themes from '../../themes/themes.js'
import api from '../../service/api.js'
import { toast } from 'react-toastify'
import MeetingActionsContextProvider from '../../contexts/meetingactions.context.js'
import { OnlineSessionsSocketStoreProvider, useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store.js'
import ReconnectHandler from '../../components/webinar-room/ReconnectHandler.jsx'


const MeetingRecorderComponent = observer( () => {

    const [isInvalidToken, setIsInvalidToken] = useState( true )
    const [hideEndTimer, setHideEndTimer] = useState( true )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { meeting_id } = useParams()
    const [searchParams] = useSearchParams()
    const recorder_token = searchParams.get( "recorder_token" )
    const recording_id = searchParams.get( "recording_id" )
    const { palette } = useTheme()

    const VirtualboardStore = useVirtualboardStore()
    const MeetingStore = useMeetingStore()

    const connectedToMeeting = useRef()
    const webinarContainer = useRef()
    const containerRef = useRef()
    const clientRef = useRef()

    if ( !clientRef.current ) {
        clientRef.current = createClient( { mode: "rtc", codec: "vp8" } )
    }

    // const { socket, emitEvent, onEvent, connectToSocketServer } = useSocketsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

    const onTimerShown = useCallback( () => {
        setHideEndTimer( false )
    }, [] )



    const hideTimer = useCallback( () => setHideEndTimer( true ), [] )


    const handleUserJoined = useCallback( async ( remoteUser ) => {
        MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )
    }, [MeetingStore] )

    const handleUserLeft = useCallback( async ( remoteUser, mediaType ) => {
        MeetingStore.removeRemoteUser( remoteUser.uid )
        if ( remoteUser.uid.includes( 'sharing' ) )
            MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
    }, [MeetingStore] )

    const handleUserPublished = useCallback( async ( remoteUser, mediaType ) => {
        await clientRef.current.subscribe( remoteUser, mediaType )

        MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )
        if ( remoteUser.uid.includes( 'sharing' ) )
            MeetingStore.addOrRemoveSharingUsers( remoteUser, "add" )
    }, [MeetingStore] )

    const handleUserUnPublished = useCallback( async ( remoteUser, mediaType ) => {
        if ( remoteUser.uid.includes( 'sharing' ) ) {
            if ( mediaType === 'video' ) {
                MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
                MeetingStore.removeRemoteUser( remoteUser.uid )
            }
        } else {
            MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )
        }
    }, [MeetingStore] )

    const autoJoinRecorderClient = useCallback( async () => {
        const localUserData = {
            name: "RECORDER",
            email: "RECORDER@EMAIL.COM",
            type: "recorder-client",
            user_id: "10101010",
            role: "recorder",
            user_role: "recorder",
        }
        MeetingStore.setLocalUserDetails( localUserData )


        if ( !connectedToMeeting.current ) {

            try {
                const { success, message, data } = await OnlineSessionSocketStore.emit( "meeting:recorder-join", { room_id: meeting_id } )

                if ( !recorder_token ) {
                    await OnlineSessionSocketStore.emit( "meeting:leave", {} )
                    return setIsInvalidToken( true )
                }
                if ( !( clientRef.current.connectionState === "CONNECTING" || clientRef.current.connectionState === "CONNECTED" ) )
                    await clientRef.current.join(
                        AGORA_CONFIG.APP_ID || "0add0da6f21f4abfaf8af383fd6c51c6",
                        meeting_id,
                        recorder_token,
                        "10101010"
                    )
                if ( Array.isArray( data.remote_users ) )
                    MeetingStore.addOrUpdateRemoteUsers( data.remote_users, 'alive-socket-server', 'set' )
                MeetingStore.setMeetingState( { isJoined: true } )
                connectedToMeeting.current = true
                const { success: vbSyncSuccess, presentation_document, elements, presenter } = OnlineSessionSocketStore.emit( "virtualboard:sync", { room_id: meeting_id } )
                if ( vbSyncSuccess ) {
                    MeetingStore.setMeetingState( { presentingVirtualBoard: true, activePresenter: presenter } )
                    MeetingStore.setPresentingDocument( presentation_document )
                    VirtualboardStore.setElements( elements || {} )
                    VirtualboardStore.setBoardState( {
                        currentSlide: presentation_document.currentSlide,
                        slides: presentation_document.slides,
                        document: presentation_document,
                    } )
                    // MeetingStore.setPresentingDocumentData( elements )
                }
            } catch ( err ) {
                console.log( err )
            }
        }
    }, [MeetingStore, VirtualboardStore, recorder_token, meeting_id, OnlineSessionSocketStore] )


    const fetchBasicDetails = useCallback( async () => {
        setLoading( true )
        try {
            const meetingFetchData = await MeetingStore.fetchMeeting( meeting_id )

            if ( recorder_token ) {
                const { data: { success } } = await api.onlineMeetings.verifyRecorderToken( meeting_id, { recorder_token: recorder_token, recording_id: recording_id } )
                if ( success ) {
                    setIsInvalidToken( false )
                    if ( meetingFetchData.success ) {
                        setError( null )
                        await OnlineSessionSocketStore.connect( meeting_id, "10101010", recorder_token, { recording_id } )
                        await autoJoinRecorderClient()
                    } else {
                        setError( meetingFetchData )
                    }
                }
            } else {
                setIsInvalidToken( true )
            }
        } catch ( err ) {
            toast( err?.response?.data?.message || err?.message || "Something went wrong, could load basic information" )
        }
        setLoading( false )
    }, [MeetingStore, meeting_id, recording_id, recorder_token, autoJoinRecorderClient, OnlineSessionSocketStore] )


    useEffect( () => {

        onCameraChanged( ( device ) => {
            console.log( "Camera Changed:", device )
        } )

        onMicrophoneChanged( ( device ) => {
            console.log( "Microphone Changed:", device )
        } )

        clientRef.current.on( "user-joined", handleUserJoined )
        clientRef.current.on( "user-left", handleUserLeft )
        clientRef.current.on( "user-published", handleUserPublished )
        clientRef.current.on( "user-unpublished", handleUserUnPublished )

    }, [handleUserJoined, handleUserLeft, handleUserPublished, handleUserUnPublished] )

    useEffect( () => {
        const unsubNewRoomMsgEvent = OnlineSessionSocketStore.on( "chat:new-room-message", ( msg ) =>
            MeetingStore.setRoomChats( msg, msg.chat_id )
        )
        const unsubNewPrivateMsgEvent = OnlineSessionSocketStore.on( "chat:new-private-message", ( msg ) =>
            MeetingStore.setRoomChats( msg, msg.chat_id )
        )

        const unsubNewUserJoinEvent = OnlineSessionSocketStore.on( "new-user-join", ( user ) => {
            console.log( "➡️ New user joined:", user.user_id )
        } )

        const unsubUserListUpdatedEvent = OnlineSessionSocketStore.on( "user-list-updated", ( { currentUsers, action } ) => {
            MeetingStore.addOrUpdateRemoteUsers( currentUsers, "alive-socket-server", action )
            MeetingStore.updateMeetingUsersDetails( currentUsers )
        }
        )

        const unsubUserLeftEvent = OnlineSessionSocketStore.on( "user-left", ( remoteUser ) => {
            MeetingStore.removeRemoteUser( remoteUser.uid, "sockets user-left" )
            if ( remoteUser.uid.includes( "sharing" ) ) {
                MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
            }
        } )

        const unsubUpdateHandRaiseQueueEvent = OnlineSessionSocketStore.on( "update-hand-raise-queue", ( { handRaises } ) => {
            MeetingStore.setRaisedHands( handRaises )
            MeetingStore.setMeetingRoomLayoutSettings( {
                hideHandsRaisedCard: false,
            } )
        } )

        const unsubExtendedSessionTimeEvent = OnlineSessionSocketStore.on( "extended-meeting-time", ( { data } ) =>
            MeetingStore.setMeetingDetails( data )
        )

        const unsubNewVbNewEventEvent = OnlineSessionSocketStore.on( "virtualboard:new-event", ( { event, elements, page_id } ) => {
            if ( event.type !== "zoom-and-pan" )
                MeetingStore.setPresentedDocElements( page_id, elements )
            else
                MeetingStore.setReadOnlyZoomAndPan( event.zoom, event.pan )

        } )

        const unsubDuplicateSessionEvent = OnlineSessionSocketStore.on( 'duplicate-session', () => {
            MeetingStore.setMeetingState( { duplicateSession: true } )
        } )

        const unsubPollUpdatedEvent = OnlineSessionSocketStore.on( 'poll:updated', ( { poll, id } ) => {
            MeetingStore.updatePoll( poll, id )
            MeetingStore.setPollResults( poll )
        } )

        const handleNewPresentation = async ( { presentation_id, presenter, presentation_document, elements = {} } ) => {

            MeetingStore.setMeetingState( { presentingVirtualBoard: true, activePresenter: presenter } )

            MeetingStore.setPresentingDocument( presentation_document )
            VirtualboardStore.setElements( elements || {} )
            VirtualboardStore.setBoardState( {
                currentSlide: presentation_document.currentSlide,
                slides: presentation_document.slides,
                document: presentation_document,
            } )
            // MeetingStore.setPresentingDocumentData( elements )
        }

        const handleVirtualboardStop = () => {
            MeetingStore.setMeetingState( { presentingVirtualBoard: false, activePresenter: null } )
            MeetingStore.setPresentingDocument( null )
            VirtualboardStore.setElements( {} )
            // MeetingStore.setPresentingDocumentData( { events: {}, elements: {} } )
        }

        const unsubNewPresentationEvent = OnlineSessionSocketStore.on( 'presentation:new-presentation', handleNewPresentation )
        const ubsubVbStopEvent = OnlineSessionSocketStore.on( "virtualboard:stoped", handleVirtualboardStop )


        const unsubMessageEvent = OnlineSessionSocketStore.on( 'message', ( message ) => toast( message ) )

        return () => {
            unsubNewRoomMsgEvent()
            unsubNewPrivateMsgEvent()
            unsubNewUserJoinEvent()
            unsubUserListUpdatedEvent()
            unsubUserLeftEvent()
            unsubExtendedSessionTimeEvent()
            unsubNewVbNewEventEvent()
            unsubDuplicateSessionEvent()
            unsubUpdateHandRaiseQueueEvent()
            unsubMessageEvent()
            unsubPollUpdatedEvent()
            unsubNewPresentationEvent()
            ubsubVbStopEvent()
        }

    }, [OnlineSessionSocketStore, VirtualboardStore, MeetingStore] )

    useEffect( () => {
        fetchBasicDetails()
    }, [fetchBasicDetails] )


    return !isInvalidToken ? (
        <Box flexGrow={1} ref={webinarContainer} overflow="auto" id="webinar-container" bgcolor={palette.background.paper} className="room-container">

            {MeetingStore.getMeetingState.isJoined && <ReconnectHandler />}

            {MeetingStore.getRemoteUsers.length === 0 && <Box display="flex" height="100%" alignItems="center" justifyContent="center" >
                <Typography textAlign="center" variant='h4'>No one is present in the meeting!</Typography>
            </Box>}

            {!loading && !error && MeetingStore.getMeetingState.isJoined && MeetingStore.getRemoteUsers.length > 0 && <Box display="flex" id="meeting-area" overflow="hidden" gap="10px" flexDirection="column" height="calc(100% - 20px)" padding="10px" >
                {hideEndTimer &&
                    <OnlineMeetingTimer fontSize="14px" showIfLessThan={600} onShow={onTimerShown} deadline={MeetingStore.getCurrentMeeting.ends_at} styles={{
                        textAlign: "center",
                        color: palette.warning.contrastText
                    }} />}
                {!hideEndTimer && <Box top="0" bgcolor={palette.error.main} borderRadius="10px" display="flex" gap="10px" alignItems="center" justifyContent="space-between" padding="5px 20px" color={palette.error.contrastText} width="100%">
                    <Typography color={palette.warning.contrastText} variant='subtitle2'>Meeting is ending in: <OnlineMeetingTimer onHide={hideTimer} fontSize="14px" showIfLessThan={600} deadline={MeetingStore.getCurrentMeeting.ends_at} styles={{
                        textAlign: "center",
                        color: palette.warning.contrastText
                    }} />
                    </Typography>
                </Box>}
                <Box id="participation" minHeight="0" flexGrow={1} ref={containerRef} width="100%" display="flex" flexShrink={1} >
                    {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' && <DiscussionMeetingParticipationLayout />}
                    {MeetingStore.getCurrentMeeting.meeting_type === 'webinar' && <WebinarMeetingParticipationLayout />}
                    {MeetingStore.getCurrentMeeting.meeting_type === 'class' && <WebinarMeetingParticipationLayout />}
                </Box >


            </Box>}

            {
                loading && !error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                    <DataLoadingSpinner size={30} fontSize="30px" waitingMessage="Loading meeting details please wait..." />
                </Box>
            }
            {
                error && <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                    <TryAgainMessageBlock code={error.status} getData={fetchBasicDetails} err={error.message} />
                </Box>
            }

        </Box >
    ) : (
        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center" overflow="auto" id="webinar-container" className="room-container viewport-height">
            <Typography variant='h1' fontSize="42px" >INVALID RECORDER CONFIGURATIONS</Typography>
        </Box>
    )
} )

const MeetingRecorder = ( props ) => {

    const { palette } = useTheme()

    return (
        <ThemeProvider theme={themes.dark}>

            <Box bgcolor={palette.layoutBackground} display="flex" flexDirection="column" className="viewport-height">
                <OnlineSessionsSocketStoreProvider>
                    <MeetingStoreProvider store={MeetingStoreInstance}>
                        <VirtualboardProvider store={VitualboardStoreInstance} >
                            {/* <SocketsContextProvider> */}
                            <MeetingActionsContextProvider>
                                <MeetingRecorderComponent {...props} />
                            </MeetingActionsContextProvider>
                            {/* </SocketsContextProvider > */}
                        </VirtualboardProvider>
                        {/* <Typography variant='h1' >RECORDER PAGE IS RECORDED</Typography> */}
                    </MeetingStoreProvider>
                </OnlineSessionsSocketStoreProvider>
            </Box >
        </ThemeProvider>
    )
}

export default MeetingRecorder

